import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Chip,
  Typography,
  Paper,
  Grid,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';

interface Serial {
  SerialNum: string;
}

interface SerialImportProps {
  value: Serial[];
  onChange: (serials: Serial[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * SerialImport component that replicates the Angular optis-import-serials directive
 * Allows manual entry, CSV import, and management of serial numbers
 */
const SerialImport: React.FC<SerialImportProps> = ({
  value = [],
  onChange,
  placeholder = 'Enter serial number and press Enter or Space',
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert serial objects to array of strings for easier manipulation
  const serialNumbers = value.map(serial => serial.SerialNum);

  /**
   * Adds a new serial number to the list
   * @param serialNum - The serial number to add
   */
  const addSerial = (serialNum: string) => {
    const trimmed = serialNum.trim();
    if (trimmed && !serialNumbers.includes(trimmed)) {
      const newSerials = [...value, { SerialNum: trimmed }];
      onChange(removeDuplicatesAndEmpty(newSerials));
    }
  };

  /**
   * Removes a serial number from the list
   * @param serialNum - The serial number to remove
   */
  const removeSerial = (serialNum: string) => {
    const newSerials = value.filter(serial => serial.SerialNum !== serialNum);
    onChange(newSerials);
  };

  /**
   * Clears all serial numbers
   */
  const clearAllSerials = () => {
    onChange([]);
  };

  /**
   * Handles keyboard input for manual entry
   * @param event - Keyboard event
   */
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (inputValue.trim()) {
        // Handle multiple serial numbers separated by commas or spaces
        const serialNumbers = inputValue
          .split(/[,\s]+/)
          .map(s => s.trim())
          .filter(s => s.length > 0);
        
        serialNumbers.forEach(serialNum => addSerial(serialNum));
        setInputValue('');
      }
    }
  };

  /**
   * Handles manual addition via button click
   */
  const handleAddClick = () => {
    if (inputValue.trim()) {
      const serialNumbers = inputValue
        .split(/[,\s]+/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
      
      serialNumbers.forEach(serialNum => addSerial(serialNum));
      setInputValue('');
    }
  };

  /**
   * Handles CSV file import
   * @param event - File input change event
   */
  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setImporting(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvData = e.target?.result as string;
        if (csvData && csvData.length > 0) {
          // Parse CSV data - remove whitespace and split by commas
          const serialNumbers = csvData
            .replace(/\s/g, '') // Remove all whitespace
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);

          if (serialNumbers.length > 0) {
            // Add all serial numbers from CSV
            serialNumbers.forEach(serialNum => addSerial(serialNum));
          } else {
            alert('No data to import or wrong file format. Please use single line comma separated values.');
          }
        } else {
          alert('No data to import or wrong file format. Please use single line comma separated values.');
        }
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('Error reading file. Please ensure it\'s a valid CSV file.');
      } finally {
        setImporting(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.onerror = () => {
      setImporting(false);
      alert('Error reading file. Please try again.');
    };

    reader.readAsText(file);
  };

  /**
   * Removes duplicates and empty serial numbers
   * @param serials - Array of serial objects
   * @returns Cleaned array
   */
  const removeDuplicatesAndEmpty = (serials: Serial[]): Serial[] => {
    const seen = new Set<string>();
    return serials.filter(serial => {
      const trimmed = serial.SerialNum.trim();
      if (trimmed === '' || seen.has(trimmed)) {
        return false;
      }
      seen.add(trimmed);
      return true;
    });
  };

  /**
   * Triggers file input click
   */
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper variant="outlined" style={{ padding: '1rem' }}>
      <Grid container spacing={2} alignItems="center">
        {/* Manual Input */}
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton
                  size="small"
                  onClick={handleAddClick}
                  disabled={disabled || !inputValue.trim()}
                >
                  <AddIcon />
                </IconButton>
              ),
            }}
          />
          <Typography variant="caption" color="textSecondary" style={{ marginTop: '0.5rem', display: 'block' }}>
            Use Enter or Space to add serial numbers. Separate multiple numbers with commas.
          </Typography>
        </Grid>

        {/* Import Controls */}
        <Grid item xs={12} md={4}>
          <Box display="flex" style={{ gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileImport}
              style={{ display: 'none' }}
              disabled={disabled}
            />
            <Tooltip title="Import CSV file with comma-separated serial numbers">
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                onClick={handleImportClick}
                disabled={disabled || importing}
              >
                {importing ? 'Importing...' : 'Import CSV'}
              </Button>
            </Tooltip>
            <Tooltip title="Clear all serial numbers">
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                startIcon={<ClearIcon />}
                onClick={clearAllSerials}
                disabled={disabled || serialNumbers.length === 0}
              >
                Clear
              </Button>
            </Tooltip>
          </Box>
        </Grid>

        {/* Serial Numbers Display */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Serial Numbers ({serialNumbers.length}):
          </Typography>
          <Box
            style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              padding: '0.5rem',
              minHeight: '60px',
              backgroundColor: '#fafafa',
            }}
          >
            {serialNumbers.length > 0 ? (
              <Box display="flex" flexWrap="wrap" style={{ gap: '8px' }}>
                {serialNumbers.map((serialNum, index) => (
                  <Chip
                    key={`${serialNum}-${index}`}
                    label={serialNum}
                    onDelete={disabled ? undefined : () => removeSerial(serialNum)}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: 'italic' }}>
                No serial numbers added yet. Use the input field above or import a CSV file.
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SerialImport; 